import {useCallback, useEffect, useState} from 'react';
import {Box, Skeleton, Stack, Typography} from '@mui/material';
import {
    AuditProgramSectionVO,
    getAuditProgramSection,
} from '@/services/auditService/auditProgramService';
import type {SectionIndexRow} from '@/pages/components/audit-program/SectionIndexView';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';

export type FilingIndexSectionViewProps = {
    sectionCode: string;
    pageCode: string;
    sectionLetter: string;
    sectionTitle: string;
    heading: string;
    rows: SectionIndexRow[];
    companyId: number;
    annualId: number;
    companyName?: string | null;
};

/** Display-only filing index (APM sheets Xi / YZi have no programme pages). */
export default function FilingIndexSectionView({
    sectionCode,
    pageCode,
    sectionLetter,
    sectionTitle,
    heading,
    rows,
    companyId,
    annualId,
    companyName,
}: FilingIndexSectionViewProps) {
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAuditProgramSection(companyId, annualId, sectionCode);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName, sectionCode]);

    useEffect(() => {
        void load();
    }, [load]);

    if (loading) {
        return <Skeleton variant="rectangular" height={360} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!header) return null;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {heading}
                </Typography>
            </Stack>
            <Box sx={{flex: 1, minHeight: 0, overflow: 'auto'}}>
                <SectionIndexView
                    header={header}
                    refCode={pageCode}
                    sectionLetter={sectionLetter}
                    sectionTitle={sectionTitle}
                    rows={rows}
                />
            </Box>
        </Box>
    );
}
